import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Message} from "primereact/message";
import {ProgressSpinner} from "primereact/progressspinner";
import {useTranslate} from "../system/useTranslate";

function LegalContentDialog({documentName, titleKey, visible, onHide}) {
    const translate = useTranslate();
    const {config} = useSelector((state) => state.app);
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!visible || !documentName) return undefined;

        const controller = new AbortController();
        setLoading(true);
        setError(false);
        fetch(`${process.env.PUBLIC_URL}/content/${documentName}.json`, {signal: controller.signal})
            .then((response) => {
                if (!response.ok) throw new Error("auth-legal-load-error");
                return response.json();
            })
            .then((payload) => {
                const language = config?.language === "fa" ? "fa" : "en";
                setContent(payload?.[language] || payload?.fa || payload?.en || null);
            })
            .catch((fetchError) => {
                if (fetchError.name !== "AbortError") setError(true);
            })
            .finally(() => setLoading(false));

        return () => controller.abort();
    }, [attempt, config?.language, documentName, visible]);

    const footer = (
        <div className="flex justify-content-end p-3 surface-50 border-top-1 border-200 border-round-bottom-2xl">
            <Button type="button" className="text-xs gap-2" icon="pi pi-check" label={translate("auth-legal-close")} onClick={onHide}/>
        </div>
    );

    return (
        <Dialog
            visible={visible}
            modal
            onHide={onHide}
            header={content?.title || translate(titleKey)}
            footer={footer}
            className={`${config?.dir || "rtl"} w-11 md:w-8 lg:w-6 border-round-bottom-2xl`}
            headerClassName="border-round-top-2xl border-bottom-1 border-200 py-3"
            contentClassName="p-4"
            pt={{footer: {className: "p-0 border-round-bottom-2xl"}}}
        >
            {loading ? (
                <div className="flex flex-column align-items-center justify-content-center gap-3 py-6">
                    <ProgressSpinner className="w-3rem h-3rem" strokeWidth="4"/>
                    <span className="text-xs text-500">{translate("auth-legal-loading")}</span>
                </div>
            ) : error || !content ? (
                <div className="flex flex-column gap-3">
                    <Message className="w-full text-xs" severity="error" text={translate("auth-legal-load-error")}/>
                    <Button type="button" className="align-self-end text-xs gap-2" icon="pi pi-refresh" outlined label={translate("auth-legal-retry")} onClick={() => setAttempt((value) => value + 1)}/>
                </div>
            ) : (
                <div className="flex flex-column gap-4">
                    {content.intro && <p className="m-0 text-xs text-600 line-height-3">{content.intro}</p>}
                    {content.sections?.map((section, index) => (
                        <section key={`${section.heading}-${index}`} className="flex align-items-start gap-3 border-bottom-1 border-100 pb-4">
                            <span className="w-3rem h-3rem flex-shrink-0 border-circle surface-100 flex align-items-center justify-content-center">
                                <i className={`${section.icon || "pi pi-info-circle"} text-xl text-primary`}/>
                            </span>
                            <div className="flex-1 flex flex-column gap-2">
                                <h3 className="m-0 text-base text-900">{section.heading}</h3>
                                {section.paragraphs?.map((paragraph, paragraphIndex) => <p key={paragraphIndex} className="m-0 text-xs text-600 line-height-3">{paragraph}</p>)}
                                {section.bullets?.length > 0 && (
                                    <ul className="m-0 pr-4 flex flex-column gap-2 text-xs text-600 line-height-3">
                                        {section.bullets.map((item, itemIndex) => <li key={itemIndex}>{item}</li>)}
                                    </ul>
                                )}
                            </div>
                        </section>
                    ))}
                </div>
            )}
        </Dialog>
    );
}

export default LegalContentDialog;
